/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {DataGrid, DataGridProps} from '@mui/x-data-grid';
import styled from 'styled-components';
import CustomGridToolbar, {CustomGridToolbarProps} from './CustomGridToolbar';
import {useCallback} from 'react';
import CustomGridPagination from './CustomGridPagination';

const StyledDataGrid: typeof DataGrid = styled(DataGrid)`
  height: 100%;
  color: white;
  border: 0;
    
  & .MuiDataGrid-overlay {
    background-color: unset;
  }
    
  & .MuiDataGrid-main {
    background-color: #202e39;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  & .MuiDataGrid-virtualScroller {
    overflow: hidden;
  }

  & .MuiDataGrid-footerContainer {
    background-color: #202e39;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  & .MuiTablePagination-root {
    color: white;
    margin-left: 0;

    & .MuiInputBase-root {
      margin-right: 16px;
      & .MuiSvgIcon-root {
        color: white;
      }
    }
  }

  & .MuiDataGrid-withBorderColor {
    border-color: #32424e;
  }

  & .MuiDataGrid-columnSeparator {
    display: none;
  }

  & .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) {
    color: #9fafbc;
  }

  & .MuiDataGrid-columnHeader--sorted {
    color: white;
  }

  & .MuiTablePagination-displayedRows {
    display: none;
  }

  & .MuiPaginationItem-textPrimary {
    color: white;
  }

  & .Mui-selected {
    background: #141d24;
  }

  & .ActionsCell-TopAligned > .MuiDataGrid-actionsCell {
    align-self: flex-start;
    height: 49px;
  }
`;

const beamInputStyle = {
  paddingBottom: 0,
  'input,select': { color: '#ffffff', fontFamily: 'Source Sans Pro', marginLeft: '2px' },
  textarea: { color: '#ffffff' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#9FAFBC',
    },
    '&:hover fieldset': {
      borderColor: 'white',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00C2DE',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  },
};

interface CustomDataGridProps {
  toolbarActionButtons?: JSX.Element[];
}

const CustomDataGrid = (props: CustomDataGridProps & CustomGridToolbarProps & DataGridProps) => {
  const CustomToolbarWithButtons = useCallback(
    () => (
      <CustomGridToolbar
        disableQuickFilter={props.disableQuickFilter}
        disableFilterButton={props.disableFilterButton}
        disableExportButton={props.disableExportButton}
        slotProps={props.slotProps}
      >
        {props.toolbarActionButtons && props.toolbarActionButtons.map(el => el)}
      </CustomGridToolbar>
    ),
    [
      props.toolbarActionButtons,
      props.disableQuickFilter,
      props.disableFilterButton,
      props.disableExportButton,
      props.slotProps,
    ],
  );

  return (
    <StyledDataGrid
      pageSizeOptions={[10, 25, 100]}
      disableColumnSelector={true}
      disableDensitySelector={true}
      disableRowSelectionOnClick={true}
      disableColumnMenu={true}
      {...props}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
        ...props.initialState,
      }}
      slots={{
        toolbar: CustomToolbarWithButtons,
        pagination: CustomGridPagination,
        ...props.slots,
      }}
      slotProps={{
        ...props.slotProps,
        baseTextField: {
          sx: beamInputStyle,
        },
        baseFormControl: {
          sx: beamInputStyle,
          variant: 'outlined',
          size: 'small',
        },
        filterPanel: {
          ...props.slotProps?.filterPanel,
          sx: {
            backgroundColor: '#1E2E3A',
            '& .MuiDataGrid-filterForm': {
              gap: '8px',
              alignItems: 'center',
            },
          },
          filterFormProps: {
            ...props.slotProps?.filterPanel?.filterFormProps,
            valueInputProps: {
              InputComponentProps: {
                variant: 'outlined',
                size: 'small',
                sx: beamInputStyle,
              },
            },
          },
        },
      }}
    />
  );
};

export default CustomDataGrid;
